import React from "react";
import { motion } from "framer-motion";
import { MessageSquare, Paperclip, Clock, Eye, Trash2 } from "lucide-react";
import { cn } from "../../../../../lib/utils";
import {
  cardInteractive,
  bodySecondary,
  motionStaggerChild,
  motionHoverLift,
} from "../../../../../lib/design-system";
import DropdownMenu from "./DropdownMenu";

const relativeTime = (date) => {
  const now = Date.now();
  const diff = now - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(date).toLocaleDateString();
};

const DiscussionCard = React.memo(
  ({ discussion, index = 0, onView, onDelete }) => {
    const menuItems = [
      {
        label: "Delete",
        icon: <Trash2 className="w-3.5 h-3.5" />,
        danger: true,
        onClick: () => onDelete(discussion._id),
      },
    ];

    const commentCount =
      discussion.commentCount ?? discussion.comments?.length ?? 0;

    return (
      <motion.div
        {...motionStaggerChild(index)}
        whileHover={motionHoverLift}
        className={cn(cardInteractive, "flex cursor-pointer group overflow-hidden")}
        onClick={() => onView(discussion._id)}
      >
        {/* Left accent bar */}
        <div className="w-1 bg-accent1 rounded-sm self-stretch flex-shrink-0" />

        <div className="flex-1 p-4 sm:p-5 min-w-0">
          <div className="flex justify-between items-start gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 sm:gap-3 mb-3">
                <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-sm bg-accent1/10 text-accent1 flex items-center justify-center text-xs font-bold flex-shrink-0 uppercase group-hover:bg-accent1/20 transition-colors">
                  {discussion.author.name.charAt(0)}
                </div>
                <div className="min-w-0 flex-1">
                  <span className="font-bold text-sm text-gray-900 dark:text-white block truncate">
                    {discussion.author.name}
                  </span>
                  <div className={cn(bodySecondary, "flex items-center gap-1")}>
                    <Clock className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">
                      {relativeTime(discussion.createdAt)}
                    </span>
                  </div>
                </div>
              </div>

              <h5 className="text-base font-bold tracking-tight text-gray-900 dark:text-white mb-1.5 line-clamp-2">
                {discussion.title}
              </h5>

              <p className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm line-clamp-2">
                {discussion.content}
              </p>
            </div>

            <div
              className="flex-shrink-0"
              onClick={(e) => e.stopPropagation()}
            >
              <DropdownMenu items={menuItems} />
            </div>
          </div>

          <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
            <div className={cn(bodySecondary, "flex items-center gap-1")}>
              <MessageSquare className="w-3.5 h-3.5" />
              <span>{commentCount} {commentCount === 1 ? "comment" : "comments"}</span>
            </div>
            {discussion.viewCount > 0 && (
              <div className={cn(bodySecondary, "flex items-center gap-1")}>
                <Eye className="w-3.5 h-3.5" />
                <span>{discussion.viewCount} {discussion.viewCount === 1 ? "view" : "views"}</span>
              </div>
            )}
            {discussion.attachments && discussion.attachments.length > 0 && (
              <div className={cn(bodySecondary, "flex items-center gap-1")}>
                <Paperclip className="w-3.5 h-3.5" />
                <span>{discussion.attachments.length}</span>
              </div>
            )}
          </div>

          <div className="h-0.5 w-0 group-hover:w-full bg-accent1 transition-all duration-300 mt-3 rounded-sm" />
        </div>
      </motion.div>
    );
  }
);

DiscussionCard.displayName = "DiscussionCard";

export default DiscussionCard;
